import React, { Suspense, useContext } from 'react'
import ReactDOM from 'react-dom/client'
import { HashRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { initializeIcons } from '@fluentui/react'

import Home from './pages/home/Home'
import Help from './pages/help/Help'
import EmailHistory from './pages/emails/EmailHistory'
import ActiveJobs from './pages/jobs/ActiveJobs'
import MarketJobs from './pages/market/MarketJobs'
import Candidates from './pages/candidates/Candidates'
import Clients from './pages/clients/Clients'
import CandidateMessagesInbox from './pages/candidateMessagesInbox/CandidateMessagesInbox'
import ActivityLogs from './pages/activityLogs/ActivityLogs'
import Layout from './pages/layout/Layout'
import Login from './pages/login/Login'
import NoPage from './pages/NoPage'
import ErrorBoundary from './components/ErrorBoundary'
import { AppStateProvider, AppStateContext } from './state/AppProvider'
import { CandidateStateProvider } from './candidate/state/CandidateProvider'

// Lazy-load the candidate portal so agency users don't pay for its code on
// first paint. Same bundle, separate chunk.
const CandidateApp = React.lazy(() => import('./candidate/CandidateApp'))

import './index.css'

initializeIcons()

const SpinnerFallback: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        border: '3px solid #f3f3f3',
        borderTop: '3px solid #0F6CBD',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
)

// Protected route component that requires authentication
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const appStateContext = useContext(AppStateContext)

  // Show loading while checking auth
  if (appStateContext?.state.isAuthLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #0F6CBD',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!appStateContext?.state.isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Public route that redirects to home if already authenticated
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const appStateContext = useContext(AppStateContext)

  // Show loading while checking auth
  if (appStateContext?.state.isAuthLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #0F6CBD',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  // Redirect to home if already authenticated
  if (appStateContext?.state.isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

const AgencyRoutes: React.FC = () => (
  <Routes>
    <Route path="/login" element={
      <PublicRoute>
        <Login />
      </PublicRoute>
    } />
    <Route path="/" element={
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    }>
      <Route index element={<Home />} />
      <Route path="help" element={<Help />} />
      <Route path="emails" element={<EmailHistory />} />
      <Route path="jobs" element={<ActiveJobs />} />
      <Route path="market" element={<MarketJobs />} />
      <Route path="candidates" element={<Candidates />} />
      <Route path="clients" element={<Clients />} />
      <Route path="candidate-messages" element={<CandidateMessagesInbox />} />
      <Route path="activity-logs" element={<ActivityLogs />} />
      <Route path="*" element={<NoPage />} />
    </Route>
  </Routes>
)

// Top-level chooser: if the hash path starts with /candidate, mount the
// candidate state tree; otherwise mount the agency state tree. This keeps
// the two auth surfaces completely isolated — they never share React state,
// and they only share the bundle + utility modules.
const RootChooser: React.FC = () => {
  const loc = useLocation()
  const isCandidate = loc.pathname === '/candidate' || loc.pathname.startsWith('/candidate/')

  if (isCandidate) {
    return (
      <ErrorBoundary fallbackTitle="Candidate portal error">
        <CandidateStateProvider>
          <Suspense fallback={<SpinnerFallback />}>
            <CandidateApp />
          </Suspense>
        </CandidateStateProvider>
      </ErrorBoundary>
    )
  }
  return (
    <ErrorBoundary fallbackTitle="Something went wrong">
      <AppStateProvider>
        <AgencyRoutes />
      </AppStateProvider>
    </ErrorBoundary>
  )
}

export default function App() {
  return (
    <HashRouter>
      <RootChooser />
    </HashRouter>
  )
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)
